import React, { useContext, useEffect, useRef, useState } from "react";
import { S3_ENDPOINT } from "../../utils";
import { AttempsContext } from "../../context/AttemptsContext";

const FileAnswer = ({ user_answers, setAnswer, question }) => {
  const { uploadProgress } = useContext(AttempsContext);
  const [spinner, setSpinner] = useState(false);
  const [useLink, setUseLink] = useState(false);
  const [inputReady, setInputReady] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  // Last url this question rendered, to tell a fresh upload from any other
  // re-render of the shared user_answers array.
  const loadedContent = useRef(null);
  const timeout = useRef(null);

  // Only this question's upload owns the progress bar.
  const progress =
    uploadProgress &&
    parseInt(uploadProgress.question_id) === parseInt(question.question_id)
      ? uploadProgress.progress
      : null;

  useEffect(() => {
    setInputReady(false);
    setTimeout(() => {
      setInputReady(true);
    }, 500);
  }, [question]);

  useEffect(() => {
    handleCurrentAnswer();
    return () => clearTimeout(timeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, user_answers]);

  const getValue = () => {
    if (hasCurrentAnswer()) {
      return currentAnswer.content;
    }
    return question.answer;
  };

  const hasCurrentAnswer = () => currentAnswer && currentAnswer !== null;

  const handleCurrentAnswer = () => {
    if (!Array.isArray(user_answers)) return;
    const current = user_answers.find(
      (answer) => parseInt(answer.question_id) === parseInt(question.question_id)
    );
    if (!current) {
      return setCurrentAnswer({});
    }
    const { content } = current;
    const isUrl = typeof content === "string";
    // A just-uploaded video needs a moment before S3 serves it; every other
    // change to user_answers (another question's answer, this one's File
    // object) must render straight through without a loading state.
    const isFreshUpload =
      question.type === "file" &&
      isUrl &&
      loadedContent.current !== null &&
      content !== loadedContent.current;
    loadedContent.current = content;
    if (isFreshUpload) {
      setSpinner(true);
      setCurrentAnswer(null);
      timeout.current = setTimeout(() => {
        setSpinner(false);
        setCurrentAnswer(current);
      }, 1500);
    } else {
      setCurrentAnswer(current);
    }
  };

  const renderValue = () => {
    if (spinner) {
      return (
        <span className="d-block mb-3">
          <div className="spinner-border small me-2" />
          Cargando video...
        </span>
      );
    }
    if (hasCurrentAnswer()) {
      if (String(currentAnswer.content).includes(S3_ENDPOINT)) {
        return (
          <div className="mb-3">
            <label>Video Cargado</label>
            {/* keyed by url so React remounts the player when the file changes */}
            <video
              controls
              key={currentAnswer.content}
              src={currentAnswer.content}
              className="mw-100 w-100"
              style={{ maxHeight: 300 }}
            />
          </div>
        );
      }
    }
  };

  const renderProgress = () => {
    if (progress === null || progress === undefined) return null;
    const done = progress >= 100;
    return (
      <div className="mb-3">
        <label className="d-block mb-1">
          {done ? "Procesando video..." : "Subiendo video..."}
        </label>
        <div
          className="progress"
          style={{ height: 18, borderRadius: 9, overflow: "hidden" }}
        >
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            style={{
              width: `${progress}%`,
              backgroundColor: "var(--primary)",
              transition: "width 0.4s ease",
            }}
            className={`progress-bar${done ? " progress-bar-striped progress-bar-animated" : ""}`}
          >
            {progress}%
          </div>
        </div>
        <small className="text-muted">
          No cierres esta ventana hasta que termine la subida.
        </small>
      </div>
    );
  };

  const renderInput = () => {
    // While this question's video is uploading, show only the progress bar.
    if (progress !== null && progress !== undefined) return null;
    if (hasCurrentAnswer() && !inputReady) {
      return (
        <button
          className="btn btn-outline-dark"
          onClick={() => setInputReady(true)}
        >
          Cambiar Archivo
        </button>
      );
    }
    if (!inputReady) return <div className="spinner-border"></div>;
    if (useLink) {
      return (
        <div>
          <label className="bold">Enlace</label>
          <input
            type="text"
            value={getValue()}
            className="form-control mb-3"
            placeholder="Copia y pega el enlace aquí"
            onChange={(e) => setAnswer(question.question_id, e.target.value)}
          />
        </div>
      );
    }
    return (
      <input
        type="file"
        accept="video/*"
        className="form-control mb-3"
        onChange={(e) => setAnswer(question.question_id, e.target.files[0])}
      />
    );
  };

  const renderInfo = () => {
    if (useLink) {
      return (
        <div className="mb-3">
          <p className="mb-2">
            Para Google Drive, asegúrate que compartas el link con la
            configuración "Cualquier persona con el enlace puede Ver"
          </p>
          <p className="mb-2">
            Para Dropbox, asegúrate que compartas el link con la configuración
            "público"
          </p>
          <p className="mb-2">
            Para YouTube, asegúrate que compartas el link con la configuración
            "No listado" o "Unlisted".
          </p>
        </div>
      );
    }
    return (
      <div className="mb-3">
        <p className="mb-0">
          Tamaño máximo de archivo: 1 GB (1000 MB).{" "}
          <span className="text-danger">
            Haz en click en "Guardar" después de elegir el archivo.
          </span>
        </p>
        <p className="mb-0">
          El video puede tardarse varios minutos en subirse.
        </p>
      </div>
    );
  };

  const renderButton = () => {
    if (useLink) {
      return (
        <p className="bold">
          Copia y Pega un Enlace{" "}
          <button
            onClick={() => setUseLink(false)}
            className="btn ms-3 btn-sm px-0 py-0 btn-link"
          >
            ¿Quieres subir un archivo?
          </button>
        </p>
      );
    }
    return (
      <p className="bold">
        Sube tu Archivo{" "}
        <button
          onClick={() => setUseLink(true)}
          className="btn ms-3 btn-sm px-0 py-0 btn-link"
        >
          ¿Quieres subir un enlace?
        </button>
      </p>
    );
  };

  return (
    <div>
      {renderValue()}
      {renderButton()}
      <h5>Instrucciones Importantes</h5>
      {renderInfo()}
      {renderProgress()}
      {renderInput()}
    </div>
  );
};

export default FileAnswer;
